import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { translation } from '../translation';
import { ThemeService } from '../service/theme.service';

@Component({
  selector: 'app-download-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './download-banner.component.html',
  styleUrl: './download-banner.component.scss'
})
export class DownloadBannerComponent implements OnInit {
  showBanner = false;
  storeLink = '';
  storeName = '';
  appName = translation.title_store || translation.title_full || translation.title;

  // Computed theme-aware colors
  bannerBg = '#0a6cf1';
  bannerText = '#ffffff';

  private themeMetaOriginal: string | null = null;

  constructor(private themeService: ThemeService) {}

  ngOnInit(): void {
    // Show only in web version
    if (Capacitor.getPlatform() === 'web') {
      // compute initial colors from current theme
      this.computeBannerColors();
      this.detectMobileOS();
    }
  }

  private detectMobileOS(): void {
    const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '').toLowerCase();

    // Do not show if previously dismissed
    const dismissed = localStorage.getItem('downloadBannerDismissed');
    if (dismissed === 'true') {
      return;
    }

    if (/iphone|ipad|ipod/.test(ua)) {
      this.storeName = 'App Store';
      this.storeLink = translation.url_app_store;
      this.setThemeColor(true);
      this.showBanner = true;
    } else if (/android/.test(ua)) {
      this.storeName = 'Google Play';
      this.storeLink = translation.url_play_store;
      this.setThemeColor(true);
      this.showBanner = true;
    }
  }

  dismissBanner(): void {
    this.showBanner = false;
    localStorage.setItem('downloadBannerDismissed', 'true');
    this.setThemeColor(false);
  }

  private setThemeColor(bannerVisible: boolean): void {
    try {
      let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'theme-color';
        document.head.appendChild(meta);
      }
      if (bannerVisible) {
        // save original only once
        if (this.themeMetaOriginal === null) {
          this.themeMetaOriginal = meta.getAttribute('content');
        }
        meta.setAttribute('content', this.bannerBg);
      } else {
        // restore
        if (this.themeMetaOriginal !== null) {
          meta.setAttribute('content', this.themeMetaOriginal);
        }
      }
    } catch (e) {
      // no-op if document/head isn't available
    }
  }

  private computeBannerColors(): void {
    const primary = this.themeService.getPrimaryColor?.() || '#0a6cf1';
    const isDark = this.themeService.isDarkMode?.() || false;
    // Create a shade that is a bit lighter on light themes and a bit darker on dark themes
    const target = isDark ? '#000000' : '#FFFFFF';
    this.bannerBg = this.mixHex(primary, target, 0.22); // 22% towards black/white
    // Choose text color for contrast
    this.bannerText = this.getLuminance(this.bannerBg) < 0.6 ? '#FFFFFF' : '#111111';
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } {
    const m = hex.replace('#', '');
    const bigint = parseInt(m.length === 3 ? m.split('').map(c => c + c).join('') : m, 16);
    return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
  }

  private rgbToHex(r: number, g: number, b: number): string {
    const toHex = (v: number) => v.toString(16).padStart(2, '0');
    return `#${toHex(Math.max(0, Math.min(255, Math.round(r))))}${toHex(Math.max(0, Math.min(255, Math.round(g))))}${toHex(Math.max(0, Math.min(255, Math.round(b))))}`;
  }

  private mixHex(a: string, b: string, weight: number): string {
    try {
      const ca = this.hexToRgb(a);
      const cb = this.hexToRgb(b);
      const r = ca.r * (1 - weight) + cb.r * weight;
      const g = ca.g * (1 - weight) + cb.g * weight;
      const bl = ca.b * (1 - weight) + cb.b * weight;
      return this.rgbToHex(r, g, bl);
    } catch {
      return a;
    }
  }

  private getLuminance(hex: string): number {
    const { r, g, b } = this.hexToRgb(hex);
    const srgb = [r, g, b].map(v => {
      const c = v / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
  }
}
